import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

/**
 * Metadatos de las rutas: qué camino tienen y qué permiso exigen.
 *
 * Este archivo NO importa componentes de página a propósito. La navegación solo
 * necesita saber qué permiso protege cada ruta, y si esa consulta arrastrara el
 * árbol entero de páginas, cualquier cambio en una vista invalidaría el bundle
 * del menú. Los elementos React viven en `routeElements.tsx`.
 *
 * Antes cada ruta llevaba su array de permisos escrito a mano dentro del JSX de
 * `App.tsx`, y el menú lateral mantenía su propia lista aparte; las dos se
 * desincronizaron. Ahora ambas leen de aquí.
 */
export interface AppRouteDef {
  /** Identificador estable. La navegación referencia rutas por este id. */
  readonly id: string;
  readonly path: string;
  /** Permiso requerido. Sin él, basta con estar autenticado. */
  readonly permission?: PermissionOrWildcard | readonly PermissionOrWildcard[];
  /** Con una lista: `any` exige uno cualquiera, `all` exige todos. */
  readonly mode?: "any" | "all";
  /** `public` no exige sesión. Por defecto `private`. */
  readonly access?: "public" | "private";
  /** Si la página se envuelve en el Layout. Por defecto sí. */
  readonly layout?: boolean;
  /** A dónde mandar cuando se deniega. Por defecto `/dashboard`. */
  readonly redirectTo?: string;
}

export const APP_ROUTES: readonly AppRouteDef[] = [
  {
    id: "login",
    path: "/login",
    access: "public",
    layout: false,
  },
  {
    // Enlace público de instalación: no exige sesión, igual que antes.
    id: "install-link",
    path: "/install-link/:installId",
    access: "public",
    layout: false,
  },
  {
    id: "dashboard",
    path: "/dashboard",
    permission: "dashboard:view",
  },
  {
    id: "dashboard-visor",
    path: "/dashboard/visor",
    permission: "dashboard:visor_view",
  },
  {
    id: "users",
    path: "/users",
    permission: "users:list",
  },
  {
    id: "licenses",
    path: "/licenses",
    permission: "licenses:view",
  },
  {
    id: "reports-business",
    path: "/reports/business",
    permission: "reports:business_view",
  },
  {
    id: "imaging",
    path: "/imaging",
    permission: "imagings:list",
  },
  {
    id: "imaging-detail",
    path: "/imaging/:imagingId",
    permission: "imagings:view",
  },
  {
    id: "laboratory",
    path: "/laboratory",
    permission: "laboratory:view",
  },
  {
    id: "bioanalysis",
    path: "/bioanalysis",
    permission: "bioanalysis:view",
  },
  {
    id: "dns",
    path: "/dns",
    permission: "dns:list",
  },
  {
    id: "settings-platform",
    path: "/settings/platform",
    permission: "settings:view",
  },
  {
    id: "servers",
    path: "/servers",
    permission: "servers:view",
  },
  {
    id: "servers-report",
    path: "/servers/report",
    permission: "servers:report_view",
  },
  {
    id: "server-detail",
    path: "/servers/:institutionUid",
    permission: "servers:view",
  },
  {
    id: "server-edit",
    path: "/servers/:institutionUid/edit",
    permission: "servers:infra_edit",
  },
];

const ROUTES_BY_ID = new Map(APP_ROUTES.map((route) => [route.id, route]));

/** Busca una ruta por su id. La navegación la usa para heredar el permiso. */
export function getRoute(routeId: string): AppRouteDef | undefined {
  return ROUTES_BY_ID.get(routeId);
}
